'use client';

import React from 'react';
import type { ReactNode, Ref } from 'react';
import { HelpCircle } from '@untitledui/icons';
import type { LabelProps as AriaLabelProps } from 'react-aria-components';
import { Label as AriaLabel } from 'react-aria-components';
import { Tooltip, TooltipTrigger } from '@milanote/roma/components/base/tooltip/tooltip';
import { cx } from '@milanote/roma/utils/cx';

const SEMANTIC_NAME = 'RomaLabel';

export interface LabelProps extends AriaLabelProps {
    children: ReactNode;
    size?: 'sm' | 'md';
    isRequired?: boolean;
    tooltip?: string;
    tooltipDescription?: string;
    ref?: Ref<HTMLLabelElement>;
}

const sizes = {
    sm: 'text-sm',
    md: 'text-md',
};

export const Label = ({ size = 'sm', isRequired, tooltip, tooltipDescription, className, ...props }: LabelProps) => {
    return (
        <AriaLabel
            // Used for conditionally hiding/showing the label element via CSS:
            // <Input label="Visible only on mobile" className="lg:**:data-label:hidden" />
            // or
            // <Input label="Visible only on mobile" className="lg:label:hidden" />
            data-label="true"
            {...props}
            className={cx(
                SEMANTIC_NAME,
                'flex cursor-default items-center gap-0.5 font-medium text-secondary',
                sizes[size],
                className,
            )}
        >
            {props.children}

            <span
                className={cx(
                    'hidden text-brand-tertiary',
                    isRequired && 'block',
                    typeof isRequired === 'undefined' && 'group-required:block',
                )}
            >
                *
            </span>

            {tooltip && (
                <Tooltip title={tooltip} description={tooltipDescription} placement="top">
                    <TooltipTrigger
                        // `TooltipTrigger` inherits the disabled state from the parent form field
                        // but we don't that. We want the tooltip be enabled even if the parent
                        // field is disabled.
                        isDisabled={false}
                        className="cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover"
                    >
                        <HelpCircle className="size-4" />
                    </TooltipTrigger>
                </Tooltip>
            )}
        </AriaLabel>
    );
};

Label.displayName = SEMANTIC_NAME;
